import { Advertiser } from '@talon/web/lib/api';
import { Box, CheckBox, FormField, Text } from 'grommet';
import { useState } from 'react';
import { CreateSelect, TalonInput } from '../..';

interface IStartTabProps {
  name: string;
  code: string;
  client?: Partial<Advertiser>;
  product: string;
  budget: number;
  clientList: Partial<Advertiser>[];
  setName: (value: string) => void;
  setCode: (value: string) => void;
  setClient: (value?: Partial<Advertiser>) => void;
  setClientList: (value: Partial<Advertiser>[]) => void;
  setProduct: (value: string) => void;
  setBudget: (value: number) => void;
  showErrors: boolean;
}

export const StartTab = ({ name, code, client, product, budget, clientList, setName, setCode, setClient,
  setClientList, setProduct, setBudget, showErrors }: IStartTabProps) => {
  const [touched, setTouched] = useState<string[]>([]);
  const show = (field: string) => showErrors || touched.includes(field);
  const touch = (field: string) => setTouched((current) => current.includes(field) ? current : [...current, field]);
  const selectClient = (clientName: string) => {
    if (!clientName) return;
    const selected = clientList.find((item) => item.name?.toLocaleLowerCase() === clientName.toLocaleLowerCase());
    if (selected) setClient(selected);
    else {
      const created = { id: 'NEW', name: clientName, isSagSignatory: false, isJpcAuthorizer: false };
      setClientList([...clientList, created]);
      setClient(created);
    }
  };
  const clientFlags = [client?.isSagSignatory && 'SAG signatory', client?.isJpcAuthorizer && 'JPC authorizer']
    .filter(Boolean).join(' · ') || 'No union flags';

  return <Box pad="medium" gap="medium">
    <Text size="14px" color="#555">Only the first three are needed now — everything else can be changed later.</Text>
    <TalonInput id="project-name" name="project-name" label="Project name" val={name} saveDelay={0}
      onChange={setName} onBlur={() => touch('name')} required
      error={show('name') && name.trim().length < 2 ? name.trim() ? 'Use at least 2 characters' : 'Add a project name' : undefined} />
    <FormField label="Client" htmlFor="select-client" error={show('client') && !client ? 'Choose or add a client' : undefined}>
      <Box style={{ position: 'relative' }}>
        <CreateSelect id="select-client" defaultOptions={clientList.map((item) => item.name || '')}
          defaultValue={client?.name} onChange={selectClient} onBlur={() => touch('client')}
          placeholder="Choose or add a client" required name="client" />
        {client?.id === 'NEW' && <Text size="11px" color="#555" style={{ position: 'absolute', right: 32,
          top: 8, background: '#EDEDED', borderRadius: 999, padding: '2px 8px', pointerEvents: 'none' }}>
          New client</Text>}
      </Box>
    </FormField>
    {client?.id === 'NEW' ? <Box gap="small">
      <Text size="12px" color="#555">This client is a…</Text>
      <Box direction="row" gap="small" wrap>
        <CheckBox label="SAG signatory" checked={!!client.isSagSignatory}
          onChange={(event) => setClient({ ...client, isSagSignatory: event.target.checked })} />
        <CheckBox label="JPC authorizer" checked={!!client.isJpcAuthorizer}
          onChange={(event) => setClient({ ...client, isJpcAuthorizer: event.target.checked })} />
      </Box>
    </Box> : client && <Box gap="xsmall">
      <Text size="14px" color="#333">{clientFlags}</Text>
      <Text size="12px" color="#777">Set on the client's record</Text>
    </Box>}
    <TalonInput id="product" name="product" label="Product" placeholder="e.g. Oat crunch bar"
      val={product} saveDelay={0} onChange={setProduct} onBlur={() => touch('product')} required
      error={show('product') && !product.trim() ? 'Add a product name' : undefined} />
    <Box direction="row-responsive" gap="medium">
      <TalonInput id="job-code" name="job-code" label="Job code · Optional" val={code}
        onChange={setCode} saveDelay={0} />
      <Box gap="xsmall">
        <TalonInput id="budget" name="budget" label="Talent budget · Optional" val={budget}
          onChange={setBudget} format="currency" currencyDecimals={false} />
        {budget > 1000000 && <Text size="12px" color="#555">
          Over $1,000,000 — the Low Budget Waiver won't apply.</Text>}
      </Box>
    </Box>
  </Box>;
};
